import React, { useCallback, useEffect, useState } from 'react';
import * as R from 'ramda';
import { loadDataHOC } from '../hoc';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { utcToZonedTime, format } from 'date-fns-tz/fp';
import { motion } from 'framer-motion';
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Divider
} from '@mui/material';
import Markdown from 'react-markdown';
import { css } from '@emotion/react';
import { useTheme } from '@mui/material/styles';
import { useCreateSubscription } from './useSubscription';
import ButtonGroupCD from './ButtonGroupCD';
import { useQueryClient } from '@cd/react-query';
import Details from './Details';
import { useQueryFetch } from './useQueryFetch';

import { indexMap } from '@cd/utils';
import { column, row } from './commonCSS';
import PaymentMethods from './PaymentMethods';
import { useStore } from './store';
import {
	SubscriptionStatus,
	getStatusInfoFromSubscriptionOrSchedule
} from './RecurPaymentStatus';

const textGroupStyle = css({
	display: 'flex',
	flex: 1,
	flexDirection: 'column'
});

const markdownStyle = css({
	padding: '8px',
	border: '1px solid var(--bgColor)',
	borderRadius: '0 4px 4px 4px',
	marginRight: '8px',
	marginTop: 0,
	fontSize: '10px',
	li: { listStyle: 'disc', listStylePosition: 'inside', fontSize: '12px' },
	h2: { marginTop: '8px' },
	em: { fontWeight: 'bold' },
	p: { marginTop: '8px' }
});

const loadDataForServices = loadDataHOC('POST', '/get/services', {
	body: JSON.stringify({}),
	reactQuery: { queryName: 'getAllServices' }
});

const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDate = date =>
	R.compose(
		format('yyyy-MM-dd'),
		x => x /*?*/,
		utcToZonedTime(currentTimeZone)
	)(date);

const filterMonthly = R.propEq('billing_type_name', 'Monthly');

const enhance = loadDataForServices;

const ServiceItem = ({
	onRecurringClicked,
	settingUpRecurring,
	isRecurring,
	product_name,
	service_name,
	amount,
	billing_type_name,
	notes = '',
	signed_date,
	repeating,
	active,
	active_date,
	index
}) => {
	const { palette } = useTheme();
	const isDark = palette.mode === 'dark';
	const bgColor = isDark ? palette.primary.main : palette.secondary.main;

	return (
		<ListItem>
			<ListItemIcon>
				{active ? (
					<CheckCircleOutlineIcon style={{ color: 'green' }} />
				) : (
					<HighlightOffIcon style={{ color: 'red' }} />
				)}
			</ListItemIcon>
			<div css={textGroupStyle}>
				<ListItemText
					sx={{ mt: 0 }}
					primary={product_name}
					secondary={`Active since: ${formatDate(active_date)}`}
				/>

				{notes && (
					<>
						<Details
							title="Details"
							main={palette.primary.main}
							bgColor={bgColor}>
							<ListItemText
								css={markdownStyle}
								style={{
									'--bgColor': bgColor
								}}
								primary={<Markdown>{notes}</Markdown>}
							/>
						</Details>
					</>
				)}
			</div>
			<ListItemText
				sx={{ flex: '0 auto' }}
				primary={
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'flex-end'
						}}>
						<Typography variant="body">{amount}</Typography>
						<Typography variant="caption">
							{billing_type_name}
						</Typography>
					</Box>
				}
			/>
		</ListItem>
	);
};

function RecurringForm({ isRecurring, setIsRecurring, isSubscribed }) {
	return (
        <Box
			sx={[row, {
				mt: 2,
				mb: 2,
				alignItems: 'center',
				alignSelf: 'stretch',
				justifyContent: 'space-between',
				gap: '8px',
				flex: 1
			}]}>
            <Typography variant="body" component="div">
				Change payment settings:
			</Typography>
            <ButtonGroupCD
				defaultIndex={isRecurring ? 1 : 0}
				size="small"
				color="primary"
				aria-label="text primary button group">
				{R.map(
					text => (
						<Button
							size="small"
							onClick={e => {
								setIsRecurring(!isRecurring);
							}}>
							{text}
						</Button>
					),
					['MANUAL', 'RECURRING']
				)}
			</ButtonGroupCD>
        </Box>
    );
}

function ButtonInfoIsland({
	setSettingUpRecurring,
	setIsRecurring,
	reset,
	isSubscribed
}) {
	return (
        <Box
			sx={[column, {
				alignSelf: 'flex-end',
				mb: 4,
				alignItems: 'flex-end'
			}]}>
            <Button
				sx={{ mb: 1 }}
				variant="outlined"
				onClick={e => {
					setSettingUpRecurring(true);
					setIsRecurring(isSubscribed);
					reset();
				}}>
				{!isSubscribed ? 'SET UP' : 'CANCEL'} RECURRING PAYMENTS
			</Button>
            <Typography
				variant="caption"
				sx={{
					maxWidth: '240px'
				}}>
				Funds {isSubscribed ? 'are' : 'will be'} automatically deducted
				from your credit card at monthly intervals to cover the service
				charges.
			</Typography>
        </Box>
    );
}

function FinishButtonIsland({
	isUpdating,
	setSettingUpRecurring,
	isRecurring,
	isSubscribed,
	onCreateSubscription
}) {
	return (
        <Box
			sx={[row, {
				gap: '8px',
				alignSelf: 'flex-end',
				mt: 4
			}]}>
            <Button
				variant="outlined"
				onClick={e => setSettingUpRecurring(false)}>
				CANCEL
			</Button>
            <Button
				color="secondary"
				disabled={isUpdating || isRecurring === isSubscribed}
				variant="outlined"
				onClick={e => onCreateSubscription(isRecurring)}>
				{!isUpdating ? (
					'FINISH'
				) : (
					<CircularProgress color="secondary" />
				)}
			</Button>
        </Box>
    );
}

const titleImageVariants = {
	open: {
		opacity: 1,
		width: 'clamp(40px, 50%, 240px)',
		transition: {
			type: 'spring',
			bounce: 0.3,
			duration: 0.5
		}
	},
	close: {
		opacity: 0.4,
		width: '84px',
		transition: {
			type: 'spring',
			bounce: 0.3,
			duration: 0.5
		}
	}
};
function TitleImage({ isOpen }) {
	return (
		<motion.img
			initial="close"
			variants={titleImageVariants}
			animate={isOpen ? 'open' : 'close'}
			css={css({
				width: 'clamp(40px, 50%, 240px)',
				alignSelf: 'self-start'
			})}
			src="/api/image/get/subscribe"
			alt="logo"
			style={{
				borderRadius: '4px'
			}}
		/>
	);
}

const RecurringPayments = ({
		isUpdating,
		setSettingUpRecurring,
		settingUpRecurring,
		onCreateSubscription,
		children,
		reset,
		...rest
	}) => {
		const { palette } = useTheme();
		const isDark = palette.mode === 'dark';

		const { data: subscriptionStatus = [] } = useQueryFetch(
			'getSubscriptionStatus',
			'/subscription/status'
		);

		const { data: paymentMethods = [] } = useQueryFetch(
			'appPaymentMethods',
			'/payment/method/list'
		);

		const [isRecurring, setIsRecurring] = useState(false);
		const isSubscribed = !R.isEmpty(subscriptionStatus);
		useEffect(() => {
			setIsRecurring(isSubscribed);
		}, [isSubscribed]);

		return (
            <>
                <Card
					elevation={isDark ? 0 : 1}
					css={column}
					sx={{ alignItems: 'flex-start' }}
					title="CHANGE RECURRING PAYMENTS SETTINGS">
					<CardContent
						css={row}
						sx={{
							alignSelf: 'stretch',
							gap: '16px',
							justifyContent: 'space-between',
							flexWrap: 'wrap'
						}}>
						<TitleImage
							isOpen={
								!(
									settingUpRecurring &&
									R.isEmpty(paymentMethods)
								)
							}
						/>

						{!settingUpRecurring && (
							<Box sx={column}>
								{!R.isEmpty(subscriptionStatus) && (
									<SubscriptionStatus
										subscriptionStatus={subscriptionStatus}
									/>
								)}
								<ButtonInfoIsland
									setSettingUpRecurring={
										setSettingUpRecurring
									}
									reset={reset}
									isSubscribed={isSubscribed}
									setIsRecurring={setIsRecurring}
								/>
							</Box>
						)}
						{settingUpRecurring && (
							<>
								{R.isEmpty(paymentMethods) && !isSubscribed ? (
									<PaymentMethods
										{...rest}
										invokedFromServiceForm={true}
										userWrapStyle={css({
											flex: 1
										})}
									/>
								) : (
									<RecurringForm
										isSubscribed={isSubscribed}
										isRecurring={isRecurring}
										setIsRecurring={setIsRecurring}
									/>
								)}
							</>
						)}
					</CardContent>
					<CardActions>
						{settingUpRecurring && (
							<FinishButtonIsland
								isUpdating={isUpdating}
								setSettingUpRecurring={setSettingUpRecurring}
								onCreateSubscription={onCreateSubscription}
								isSubscribed={isSubscribed}
								isRecurring={isRecurring}
							/>
						)}
					</CardActions>
				</Card>
                <Typography component="div" variant="caption" sx={{ mt: 1 }}>
					{!settingUpRecurring
						? 'Your services'
						: 'Showing monthly services eligible for recurring payments'}
				</Typography>
                {React.Children.map(children, child =>
					React.cloneElement(child, {
						filter: settingUpRecurring ? filterMonthly : R.identity
					})
				)}
            </>
        );
	};

const ServiceListItem = ({ data, index }) => {
	return (
		<>
			<Divider />
			<ServiceItem key={index} {...data} index={index} />
		</>
	);
};
function ServiceList({ data, filter = R.identity }) {
	return (
		<List>
			{R.compose(
				indexMap((data, index) => (
					<ServiceListItem key={index} data={data} index={index} />
				)),
				R.filter(filter)
			)(data)}
		</List>
	);
}

function Services({ data, filter: noop }) {
	const [settingUpRecurring, setSettingUpRecurring] = useState(false); // TODO: set it back to false
	const { data: paramData, isFetching } = useQueryFetch(
		'appRecurringPaymentsEnabled',
		'/recurring/payments/enabled',
		'GET'
	);

	const queryClient = useQueryClient();
	const { dispatch } = useStore();
	const {
		mutate,
		reset,
		isLoading: isUpdating
	} = useCreateSubscription({
		onSuccess: data => {
			const { status, start_date } =
				getStatusInfoFromSubscriptionOrSchedule(data);
			setSettingUpRecurring(false);
			queryClient.invalidateQueries('getSubscriptionStatus');
			const isCreated = !(status === 'canceled');
			const dateMsg = isCreated ? `Start Date: ${start_date}` : '';

			dispatch({
				type: 'post success',
				message: `Recurring payments status: ${status}. ${dateMsg}`
			});
		},
		onError: e => {
			setSettingUpRecurring(false);
			queryClient.invalidateQueries('getSubscriptionStatus');
		}
	});

	useEffect(() => {
		reset({ subscriptionData: [] });
	}, []);

	const onCreateSubscription = useCallback(isRecurring => {
		const data = queryClient.getQueryData('getAllServices');

		const subscriptionData = R.compose(
			R.reduce(
				(acc, productID) => ({ ...acc, [productID]: isRecurring }),
				{}
			),
			R.pluck('product_id'),
			R.filter(filterMonthly)
		)(data);

		console.log('subscriptionData', subscriptionData);

		mutate({ subscriptionData });
		//setSettingUpRecurring(!settingUpRecurring);
	}, []);

	if (isFetching) {
		return <CircularProgress />;
	}

	return (
        <Box sx={column}>
            <Typography variant="h6">Service List</Typography>
            <Typography variant="caption" component="div" sx={{ mb: 4 }}>
				View your services and products.
			</Typography>
            {paramData?.RECURRING_PAYMENTS_ENABELD ? (
				<RecurringPayments
					isUpdating={isUpdating}
					reset={reset}
					settingUpRecurring={settingUpRecurring}
					setSettingUpRecurring={setSettingUpRecurring}
					onCreateSubscription={onCreateSubscription}>
					<ServiceList data={data} />
				</RecurringPayments>
			) : (
				<ServiceList data={data} />
			)}
        </Box>
    );
}

export default enhance(Services);
